// Import react-pdf styles for selectable text & links
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

import { Button, cn, Tooltip } from '@heroui/react'
import { ArrowUp, ChevronLeft, ChevronRight, RotateCcw, ZoomIn, ZoomOut } from 'lucide-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'

// Set up PDF.js worker - point PDF.js to its worker file
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString()

interface PDFPreviewProps {
  url: string
}

export default function PDFPreview({ url }: PDFPreviewProps) {
  const [numPages, setNumPages] = useState<number>(0)
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [zoomFactor, setZoomFactor] = useState<number>(1)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [showScrollToTop, setShowScrollToTop] = useState<boolean>(false)
  const [pageInputValue, setPageInputValue] = useState<string>('')
  const [visiblePages, setVisiblePages] = useState<Set<number>>(new Set([1, 2, 3, 4, 5, 6]))
  const [actualPageHeight, setActualPageHeight] = useState<number>(806)
  const [actualPageWidth, setActualPageWidth] = useState<number>(600)

  // Removed pageInfos reference
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)

  // Page gap between pages
  const PAGE_GAP = 16

  // Handle PDF load success
  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
    setIsLoading(false)
    setError(null)
    setPageInputValue('1')
  }, [])

  // Handle PDF load error
  const onDocumentLoadError = useCallback((error: Error) => {
    console.error('PDF load error:', error)
    if (error.message.includes('worker') || error.message.includes('fetch')) {
      setError('PDF viewer temporarily unavailable. Please try again or download the file.')
    } else {
      setError('Failed to load PDF. The file may be corrupted or unavailable.')
    }
    setIsLoading(false)
  }, [])

  // Handle page load success to get actual page dimensions
  const onPageLoadSuccess = useCallback((page: any) => {
    const viewport = page.getViewport({ scale: 1 })

    setActualPageWidth(viewport.width + 2)
    setActualPageHeight(viewport.height + 2)
  }, [])

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    setZoomFactor(prev => Math.min(prev * 1.2, 3))
  }, [])

  const handleZoomOut = useCallback(() => {
    setZoomFactor(prev => Math.max(prev / 1.2, 0.3))
  }, [])

  const handleReset = useCallback(() => {
    setZoomFactor(1)
  }, [])

  // Scroll to top function
  const scrollToTop = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: 'smooth' })
      setPageNumber(1)
      setPageInputValue('1')
    }
  }, [])

  // Calculate scroll position for a specific page - more precise calculation
  const calculateScrollPosition = useCallback(
    (targetPage: number) => {
      if (targetPage < 1 || targetPage > numPages) return 0

      // Calculate the exact scroll position for the target page
      // Each page takes up: (actualPageHeight * zoomFactor) + PAGE_GAP
      const pageHeightWithZoom = actualPageHeight * zoomFactor + PAGE_GAP

      const scrollPosition = (targetPage - 1) * pageHeightWithZoom + PAGE_GAP

      return scrollPosition
    },
    [numPages, actualPageHeight, zoomFactor],
  )

  // Navigate to specific page
  const goToPage = useCallback(
    (targetPage: number) => {
      if (targetPage < 1 || targetPage > numPages) return

      const scrollPosition = calculateScrollPosition(targetPage)

      if (containerRef.current) {
        containerRef.current.scrollTo({ top: scrollPosition, behavior: 'instant' })
      }
      setPageNumber(targetPage)
      setPageInputValue(targetPage.toString())
    },
    [calculateScrollPosition, numPages],
  )

  // Handle page input changes
  const handlePageInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPageInputValue(e.target.value)
  }, [])

  // Handle page input blur and enter key
  const handlePageInputBlur = useCallback(() => {
    const pageNum = parseInt(pageInputValue)

    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= numPages) {
      goToPage(pageNum)
    } else {
      setPageInputValue(pageNumber.toString())
    }
  }, [pageInputValue, numPages, pageNumber, goToPage])

  const handlePageInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handlePageInputBlur()
      }
    },
    [handlePageInputBlur],
  )

  // Navigation functions
  const handlePreviousPage = useCallback(() => {
    if (pageNumber > 1) {
      const targetPage = pageNumber - 1

      setPageInputValue(targetPage.toString())
      setPageNumber(targetPage)

      goToPage(targetPage)
    }
  }, [goToPage, pageNumber])

  const handleNextPage = useCallback(() => {
    if (pageNumber < numPages) {
      const targetPage = pageNumber + 1

      setPageInputValue(targetPage.toString())
      setPageNumber(targetPage)

      goToPage(targetPage)
    }
  }, [goToPage, pageNumber, numPages])

  // Handle scroll to update current page number and visible pages
  const handleScroll = useCallback(() => {
    if (!containerRef.current || numPages === 0) return

    const scrollTop = containerRef.current.scrollTop
    const containerHeight = containerRef.current.clientHeight

    // Calculate which page is currently visible using precise math
    const pageHeightWithZoom = actualPageHeight * zoomFactor
    const pageHeightWithGap = pageHeightWithZoom + PAGE_GAP

    // Calculate current page based on scroll position
    const currentPage = Math.floor(scrollTop / pageHeightWithGap) + 1
    const clampedPage = Math.max(1, Math.min(currentPage, numPages))

    if (clampedPage !== pageNumber) {
      setPageNumber(clampedPage)
      setPageInputValue(clampedPage.toString())
    }

    // Show/hide scroll to top button
    setShowScrollToTop(scrollTop > 200)

    // Calculate visible pages for virtual rendering
    const visiblePagesSet = new Set<number>()
    const startPage = Math.max(1, Math.floor(scrollTop / pageHeightWithGap))
    const endPage = Math.min(numPages, Math.ceil((scrollTop + containerHeight) / pageHeightWithGap))

    // Add buffer pages: 3 before and 3 after the visible viewport
    const bufferBefore = Math.max(1, startPage - 10)
    const bufferAfter = Math.min(numPages, endPage + 10)

    for (let i = bufferBefore; i <= bufferAfter; i++) {
      visiblePagesSet.add(i)
    }

    setVisiblePages(visiblePagesSet)
  }, [numPages, actualPageHeight, zoomFactor, pageNumber])

  useEffect(() => {
    goToPage(pageNumber)
  }, [zoomFactor, goToPage])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement) return // Don't interfere with input fields

      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        handlePreviousPage()
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        handleNextPage()
      } else if (e.key === 'Home') {
        e.preventDefault()
        goToPage(1)
      } else if (e.key === 'End') {
        e.preventDefault()
        goToPage(numPages)
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handlePreviousPage, handleNextPage, goToPage, numPages])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current)
    }
  }, [])

  // Debounced scroll handler for performance
  useEffect(() => {
    const debouncedScrollHandler = () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
      scrollTimeoutRef.current = setTimeout(handleScroll, 16) // ~60fps
    }

    const container = containerRef.current

    if (container) {
      container.addEventListener('scroll', debouncedScrollHandler, { passive: true })

      return () => container.removeEventListener('scroll', debouncedScrollHandler)
    }
  }, [handleScroll])

  if (error) {
    return (
      <div className="max-w-xl text-center text-white">
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-red-500/15 text-red-400">
          <img src="" />
        </div>
        <h3 className="mb-2 text-xl font-semibold">Unable to preview PDF</h3>
        <p className="text-neutral-200">{error}</p>
      </div>
    )
  }

  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center px-8">
      {/* PDF Container */}
      <div
        ref={containerRef}
        className={cn(
          'relative flex-1 flex overflow-auto preview-content scroll-smooth',
          isLoading && 'items-center justify-center',
        )}
        style={{
          scrollBehavior: 'smooth',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
          overflowAnchor: 'none',
        }}
      >
        {isLoading && (
          <div className="text-center">
            <div className="mx-auto mb-4 h-14 w-14 animate-spin rounded-full border-b-2 border-neutral-300" />
            <p className="text-base font-medium text-neutral-100">Loading PDF...</p>
          </div>
        )}

        <Document
          key={`pdf-${url}`}
          error={null}
          file={url}
          loading={null}
          onLoadError={onDocumentLoadError}
          onLoadSuccess={onDocumentLoadSuccess}
        >
          {!isLoading && numPages > 0 && (
            <div className="flex flex-col items-center gap-4 px-4">
              {Array.from(new Array(numPages), (_, index) => {
                const pageNum = index + 1
                const isVisible = visiblePages.has(pageNum)

                // Only render visible pages for performance
                if (!isVisible && numPages > 25) {
                  return (
                    <div
                      key={pageNum}
                      className="bg-neutral-800 rounded-sm flex items-center justify-center text-white/70"
                      style={{
                        width: `${actualPageWidth * zoomFactor}px`,
                        height: `${actualPageHeight * zoomFactor}px`,
                      }}
                    >
                      Page {pageNum}
                    </div>
                  )
                }

                return (
                  <Page
                    key={pageNum}
                    renderAnnotationLayer
                    renderTextLayer
                    pageNumber={pageNum}
                    scale={zoomFactor}
                    className={cn(
                      'shadow-lg border border-white/10 rounded-sm',
                      numPages === index + 1 && 'mb-24',
                    )}
                    loading={
                      <div
                        className="bg-neutral-800 animate-pulse rounded-sm flex items-center justify-center text-white/70"
                        style={{
                          width: `${actualPageWidth * zoomFactor}px`,
                          height: `${actualPageHeight * zoomFactor}px`,
                        }}
                      >
                        Loading page {pageNum}...
                      </div>
                    }
                    onLoadSuccess={onPageLoadSuccess}
                  />
                )
              })}
            </div>
          )}
        </Document>
      </div>

      {/* Scroll to Top Button */}
      {showScrollToTop && (
        <Tooltip content="Scroll to top" placement="left">
          <Button
            isIconOnly
            aria-label="Scroll to top"
            className="fixed bottom-24 right-8 bg-black/60 hover:bg-black/80 text-white/90 backdrop-blur-md shadow-lg border border-white/15"
            size="lg"
            variant="flat"
            onPress={scrollToTop}
          >
            <ArrowUp className="h-5 w-5" />
          </Button>
        </Tooltip>
      )}

      {/* Bottom Controls */}
      <div className="pointer-events-none absolute inset-x-0 bottom-[max(16px,env(safe-area-inset-bottom))] flex justify-center bottom-controls z-10">
        <div className="pointer-events-auto flex items-center gap-1 rounded-full border border-white/15 bg-black/60 px-2 py-1 backdrop-blur-md shadow-lg">
          {/* Zoom Controls */}
          <Tooltip content="Zoom out" placement="top">
            <Button
              isIconOnly
              aria-label="Zoom out"
              className="text-white/90 hover:bg-white/10"
              size="sm"
              variant="light"
              onPress={handleZoomOut}
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
          </Tooltip>

          <span className="mx-1 w-12 text-center text-xs text-white/90 tabular-nums">
            {Math.round(zoomFactor * 100)}%
          </span>

          <Tooltip content="Zoom in" placement="top">
            <Button
              isIconOnly
              aria-label="Zoom in"
              className="text-white/90 hover:bg-white/10"
              size="sm"
              variant="light"
              onPress={handleZoomIn}
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
          </Tooltip>

          <div className="mx-1 h-4 w-px bg-white/20" />

          <Tooltip content="Reset zoom" placement="top">
            <Button
              isIconOnly
              aria-label="Reset zoom"
              className="text-white/90 hover:bg-white/10"
              size="sm"
              variant="light"
              onPress={handleReset}
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          </Tooltip>

          <div className="mx-1 h-4 w-px bg-white/20" />

          {/* Page Navigation */}
          <Tooltip content="Previous page" placement="top">
            <Button
              isIconOnly
              aria-label="Previous page"
              className="text-white/90 hover:bg-white/10 disabled:opacity-50"
              isDisabled={pageNumber <= 1}
              size="sm"
              variant="light"
              onPress={handlePreviousPage}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          </Tooltip>

          <div className="flex items-center gap-1 text-white/90 input-number-wrapper">
            <input
              aria-label="Page number"
              className="w-12 text-center text-xs bg-transparent border-none outline-none text-white/90 tabular-nums number-input"
              max={numPages}
              min={1}
              type="number"
              value={pageInputValue}
              onBlur={handlePageInputBlur}
              onChange={handlePageInputChange}
              onKeyDown={handlePageInputKeyDown}
            />
            <div className="text-xs">/</div>
            <div className="w-12 text-xs text-center">{numPages}</div>
          </div>

          <Tooltip content="Next page" placement="top">
            <Button
              isIconOnly
              aria-label="Next page"
              className="text-white/90 hover:bg-white/10 disabled:opacity-50"
              isDisabled={pageNumber >= numPages}
              size="sm"
              variant="light"
              onPress={handleNextPage}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </Tooltip>
        </div>
      </div>
    </div>
  )
}
